import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';

/**
 * The largest slice of time one frame is allowed to advance the field by.
 *
 * A tab left in the background, or a long main-thread task, hands the next frame
 * a gap of seconds rather than milliseconds. Without a ceiling every image would
 * jump most of the way across the section at once and several would tunnel
 * straight through a wall, because the step is larger than the room they had.
 */
const MAX_STEP = 1 / 30;

/**
 * @typedef {object} FieldItem
 * @property {HTMLElement} el - The image wrapper being moved.
 * @property {number} x - Distance from the field's left edge, in pixels.
 * @property {number} y - Distance from the field's top edge, in pixels.
 * @property {number} vx - Horizontal speed, in pixels per second.
 * @property {number} vy - Vertical speed, in pixels per second.
 * @property {number} minX - Leftmost position the image may take.
 * @property {number} maxX - Rightmost position the image may take.
 * @property {number} minY - Topmost position the image may take.
 * @property {number} maxY - Bottommost position the image may take.
 * @property {number} rangeX - `maxX - minX`, the width of the run.
 * @property {number} rangeY - `maxY - minY`, the height of the run.
 * @property {boolean} paused - Whether this one image is currently held still.
 */

/**
 * A field of images that drift across the section and bounce off its edges.
 *
 * Each image carries its own position and velocity and is checked only against
 * the walls — never against its neighbours. That is deliberate: the images pass
 * over one another, so a slow one is never shoved out of the composition by a
 * fast one, and the whole field stays as evenly spread as the merchant arranged
 * it. It also means the cost is linear in the number of images rather than
 * quadratic, so a merchant can keep adding them.
 *
 * Positions are written to `--socials-dx` / `--socials-dy` and consumed by the
 * `translate` property, so every frame is a compositor job: no layout, no paint,
 * and nothing that reads back from the DOM. Rotation is left entirely to CSS on
 * the `rotate` property, which composes with `translate` without either side
 * having to know about the other — so tilt is a setting the script never touches.
 *
 * A tilted image reaches further than its layout box, so the walls are pulled in
 * by the overhang of its rotated bounding box. Without that, a corner would
 * disappear off the edge before the bounce.
 *
 * Before this runs — and for anyone who has asked for reduced motion — the
 * images simply sit at the positions the merchant placed them at, which is the
 * arrangement in the design.
 *
 * @extends {Component}
 */
class SocialsFieldComponent extends Component {
  /** @type {FieldItem[]} */
  #items = [];

  /** @type {number} */
  #frame = 0;

  /** @type {number} */
  #last = 0;

  /** @type {boolean} */
  #running = false;

  /** @type {boolean} */
  #onScreen = true;

  /** @type {boolean} */
  #held = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) return;

    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(this);

    this.#intersectionObserver = new IntersectionObserver(this.#onIntersect);
    this.#intersectionObserver.observe(this);

    // Only where hovering is a real gesture. On a touch screen `pointerover`
    // fires on tap, which would strand an image the visitor meant to open.
    if (matchMedia('(hover: hover)').matches) {
      this.addEventListener('pointerover', this.#onPointerOver);
      this.addEventListener('pointerout', this.#onPointerOut);
    }

    this.addEventListener('shopify:block:select', this.#hold);
    this.addEventListener('shopify:block:deselect', this.#release);

    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerout', this.#onPointerOut);
    this.removeEventListener('shopify:block:select', this.#hold);
    this.removeEventListener('shopify:block:deselect', this.#release);
  }

  /**
   * Rebuilds the field for the current size.
   *
   * Runs on connect and on every resize, because both the walls and the images
   * are sized from the section: a narrower screen gives a shorter run, and an
   * image whose width is a share of that section is smaller too.
   *
   * An image already in flight keeps where it had got to — as a fraction of its
   * run rather than a pixel count, so a rotated phone leaves the field looking
   * rearranged rather than piled into a corner. One that wasn't being tracked,
   * because the merchant hides it on small screens, starts again from where it
   * was placed.
   */
  #measure = () => {
    const width = this.clientWidth;
    const height = this.clientHeight;

    if (!width || !height) {
      this.#stop();
      return;
    }

    const previous = new Map(this.#items.map((item) => [item.el, item]));
    const pace = parseFloat(getComputedStyle(this).getPropertyValue('--socials-pace')) || 30;

    this.#items = [];

    for (const element of this.querySelectorAll('[data-socials-item]')) {
      const el = /** @type {HTMLElement} */ (element);

      // No boxes means the layout has taken it out — an image the merchant
      // turned off for phones. Leave it alone rather than flying it about
      // behind `display: none`.
      if (!el.getClientRects().length) continue;

      const boxWidth = el.offsetWidth;
      const boxHeight = el.offsetHeight;

      // Nothing to bounce yet. A picture whose dimensions the markup didn't
      // carry has no height until it decodes, and the resize observer brings
      // this straight back round once it does.
      if (!boxWidth || !boxHeight) continue;

      const { overhangX, overhangY } = rotatedOverhang(el, boxWidth, boxHeight);

      const item = {
        el,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        minX: overhangX,
        maxX: width - boxWidth - overhangX,
        minY: overhangY,
        maxY: height - boxHeight - overhangY,
        rangeX: 0,
        rangeY: 0,
        paused: false,
      };

      // An image too big for the section it sits in has nowhere to travel.
      // Park it in the middle of that axis instead of letting the walls cross
      // over and bounce it on every frame.
      if (item.maxX < item.minX) item.minX = item.maxX = (width - boxWidth) / 2;
      if (item.maxY < item.minY) item.minY = item.maxY = (height - boxHeight) / 2;

      item.rangeX = item.maxX - item.minX;
      item.rangeY = item.maxY - item.minY;

      const before = previous.get(el);

      if (before) {
        item.x = item.minX + fraction(before.x, before.minX, before.rangeX) * item.rangeX;
        item.y = item.minY + fraction(before.y, before.minY, before.rangeY) * item.rangeY;
        item.vx = before.vx;
        item.vy = before.vy;
        item.paused = before.paused;
      } else {
        const placement = readPlacement(el);
        const radians = (placement.direction * Math.PI) / 180;
        const speed = pace * placement.speed;

        item.x = clamp((width * placement.x) / 100 - boxWidth / 2, item.minX, item.maxX);
        item.y = clamp((height * placement.y) / 100 - boxHeight / 2, item.minY, item.maxY);
        item.vx = Math.cos(radians) * speed;
        item.vy = Math.sin(radians) * speed;
      }

      if (!item.rangeX) item.vx = 0;
      if (!item.rangeY) item.vy = 0;

      write(item);
      this.#items.push(item);

      // Watched as well as the field itself: a picture that arrives without its
      // dimensions in the markup changes height on decode, which moves the wall
      // it should be turning at but leaves the field exactly as it was.
      this.#resizeObserver?.observe(el);
    }

    // Only now are the images carrying a position of their own, so this is the
    // point at which CSS can hand placement over from `left`/`top` to the
    // translation — with the two agreeing, there is nothing to see in the swap.
    this.toggleAttribute('data-socials-moving', true);

    this.#start();
  };

  #start() {
    if (this.#running || this.#held || !this.#onScreen || !this.#items.length) return;

    this.#running = true;
    this.#last = performance.now();
    this.#frame = requestAnimationFrame(this.#step);
  }

  #stop() {
    cancelAnimationFrame(this.#frame);
    this.#running = false;
  }

  /**
   * Advances every image by the time since the last frame and turns the ones
   * that have reached a wall.
   *
   * The bounce reflects the overshoot back into the run rather than snapping to
   * the edge, so the image leaves the wall having travelled exactly as far as it
   * should have — the pace stays even instead of stalling for a frame each time.
   *
   * @param {number} now - The timestamp handed over by the frame callback.
   */
  #step = (now) => {
    const elapsed = Math.min((now - this.#last) / 1000, MAX_STEP);

    this.#last = now;

    for (const item of this.#items) {
      if (item.paused) continue;

      item.x += item.vx * elapsed;
      item.y += item.vy * elapsed;

      if (item.x < item.minX) {
        item.x = item.minX + Math.min(item.minX - item.x, item.rangeX);
        item.vx = -item.vx;
      } else if (item.x > item.maxX) {
        item.x = item.maxX - Math.min(item.x - item.maxX, item.rangeX);
        item.vx = -item.vx;
      }

      if (item.y < item.minY) {
        item.y = item.minY + Math.min(item.minY - item.y, item.rangeY);
        item.vy = -item.vy;
      } else if (item.y > item.maxY) {
        item.y = item.maxY - Math.min(item.y - item.maxY, item.rangeY);
        item.vy = -item.vy;
      }

      write(item);
    }

    this.#frame = requestAnimationFrame(this.#step);
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = ([entry]) => {
    this.#onScreen = entry?.isIntersecting ?? true;

    if (this.#onScreen) {
      this.#start();
    } else {
      this.#stop();
    }
  };

  /**
   * Holds the whole field still while the theme editor has a block selected, so
   * a merchant is not editing an image that is sliding out from under them.
   */
  #hold = () => {
    this.#held = true;
    this.#stop();
  };

  #release = () => {
    this.#held = false;
    this.#start();
  };

  /** @param {Event} event */
  #onPointerOver = (event) => {
    const item = this.#itemAt(event.target);

    if (item) this.#setPaused(item, true);
  };

  /**
   * `pointerout` also fires on the way into a child, where the pointer has not
   * actually left the image — the related target tells the two apart.
   *
   * @param {PointerEvent} event
   */
  #onPointerOut = (event) => {
    const item = this.#itemAt(event.target);

    if (!item) return;
    if (event.relatedTarget instanceof Node && item.el.contains(event.relatedTarget)) return;

    this.#setPaused(item, false);
  };

  /**
   * @param {EventTarget | null} target
   * @returns {FieldItem | undefined}
   */
  #itemAt(target) {
    if (!(target instanceof Element)) return undefined;

    const el = target.closest('[data-socials-item]');

    return this.#items.find((item) => item.el === el);
  }

  /**
   * @param {FieldItem} item
   * @param {boolean} paused
   */
  #setPaused(item, paused) {
    item.paused = paused;
    item.el.toggleAttribute('data-socials-held', paused);
  }
}

/**
 * Writes an image's position for the compositor to pick up.
 *
 * @param {FieldItem} item - The image to place.
 */
function write(item) {
  item.el.style.setProperty('--socials-dx', `${item.x}px`);
  item.el.style.setProperty('--socials-dy', `${item.y}px`);
}

/**
 * How far a rotated box reaches beyond its layout box on each axis.
 *
 * The rotation is read back from CSS rather than passed in, so the section's
 * "tilt the images" setting is obeyed by the walls without the script knowing
 * the setting exists.
 *
 * @param {HTMLElement} el - The image wrapper.
 * @param {number} width - Its unrotated width.
 * @param {number} height - Its unrotated height.
 * @returns {{ overhangX: number, overhangY: number }} Overhang per side, in pixels.
 */
function rotatedOverhang(el, width, height) {
  const rotate = getComputedStyle(el).rotate;
  const degrees = rotate && rotate !== 'none' ? parseFloat(rotate) : 0;

  if (!degrees) return { overhangX: 0, overhangY: 0 };

  const radians = (degrees * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));

  return {
    overhangX: (width * cos + height * sin - width) / 2,
    overhangY: (width * sin + height * cos - height) / 2,
  };
}

/**
 * Reads the placement a merchant gave an image in the theme editor.
 *
 * @param {HTMLElement} el - The image wrapper.
 * @returns {{ x: number, y: number, direction: number, speed: number }} Start point, heading and pace multiplier.
 */
function readPlacement(el) {
  return {
    x: Number(el.dataset.socialsX) || 0,
    y: Number(el.dataset.socialsY) || 0,
    direction: Number(el.dataset.socialsDirection) || 0,
    speed: (Number(el.dataset.socialsSpeed) || 100) / 100,
  };
}

/**
 * Where a position sits along its run, as a fraction between 0 and 1.
 *
 * @param {number} value - The position.
 * @param {number} min - The start of the run.
 * @param {number} range - The length of the run.
 * @returns {number} The fraction, or the midpoint for a run with no length.
 */
function fraction(value, min, range) {
  if (range <= 0) return 0.5;

  return clamp((value - min) / range, 0, 1);
}

if (!customElements.get('socials-field')) {
  customElements.define('socials-field', SocialsFieldComponent);
}
